import { useCallback, useEffect, useState } from "react";

import type {
  Board,
  BoardCard,
  BoardColumn,
  BoardMember,
  CardInput,
} from "@/features/board/types";
import { apiJson } from "@/lib/api";

export const useBoard = (projectId: string) => {
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBoard(await apiJson<Board>(`/api/projects/${projectId}/board`));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The board could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createCard = async (input: CardInput) => {
    const { card } = await apiJson<{ card: BoardCard }>(
      `/api/projects/${projectId}/cards`,
      { method: "POST", body: JSON.stringify(input) }
    );
    setBoard((current) =>
      current
        ? {
            ...current,
            columns: current.columns.map((column) =>
              column.id === card.columnId
                ? { ...column, cards: [...column.cards, card] }
                : column
            ),
          }
        : current
    );
    return card;
  };

  const updateCard = async (cardId: string, input: Omit<CardInput, "columnId">) => {
    const { card } = await apiJson<{ card: BoardCard }>(
      `/api/projects/${projectId}/cards/${cardId}`,
      { method: "PATCH", body: JSON.stringify(input) }
    );
    setBoard((current) =>
      current
        ? {
            ...current,
            columns: current.columns.map((column) => ({
              ...column,
              cards: column.cards.map((item) => (item.id === card.id ? card : item)),
            })),
          }
        : current
    );
    return card;
  };

  const deleteCard = async (cardId: string) => {
    await apiJson<{ ok: true }>(`/api/projects/${projectId}/cards/${cardId}`, {
      method: "DELETE",
    });
    setBoard((current) =>
      current
        ? {
            ...current,
            columns: current.columns.map((column) => ({
              ...column,
              cards: column.cards
                .filter((item) => item.id !== cardId)
                .map((item, position) => ({ ...item, position })),
            })),
          }
        : current
    );
  };

  const createColumn = async (name: string) => {
    const { column } = await apiJson<{ column: BoardColumn }>(
      `/api/projects/${projectId}/columns`,
      { method: "POST", body: JSON.stringify({ name }) }
    );
    setBoard((current) =>
      current ? { ...current, columns: [...current.columns, column] } : current
    );
    return column;
  };

  const renameColumn = async (columnId: string, name: string) => {
    const { column } = await apiJson<{ column: Omit<BoardColumn, "cards"> }>(
      `/api/projects/${projectId}/columns/${columnId}`,
      { method: "PATCH", body: JSON.stringify({ name }) }
    );
    setBoard((current) =>
      current
        ? {
            ...current,
            columns: current.columns.map((item) =>
              item.id === columnId ? { ...item, ...column } : item
            ),
          }
        : current
    );
  };

  const deleteColumn = async (columnId: string) => {
    await apiJson<{ ok: true }>(`/api/projects/${projectId}/columns/${columnId}`, {
      method: "DELETE",
    });
    setBoard((current) =>
      current
        ? {
            ...current,
            columns: current.columns
              .filter((column) => column.id !== columnId)
              .map((column, position) => ({ ...column, position })),
          }
        : current
    );
  };

  const addMember = async (username: string) => {
    const { member } = await apiJson<{ member: BoardMember }>(
      `/api/projects/${projectId}/members`,
      { method: "POST", body: JSON.stringify({ username }) }
    );
    setBoard((current) =>
      current ? { ...current, members: [...current.members, member] } : current
    );
    return member;
  };

  const removeMember = async (userId: string) => {
    await apiJson<{ ok: true }>(`/api/projects/${projectId}/members/${userId}`, {
      method: "DELETE",
    });
    setBoard((current) =>
      current
        ? {
            ...current,
            members: current.members.filter((member) => member.id !== userId),
            columns: current.columns.map((column) => ({
              ...column,
              cards: column.cards.map((card) =>
                card.assigneeId === userId ? { ...card, assigneeId: null } : card
              ),
            })),
          }
        : current
    );
  };

  const reorderCards = async (columns: BoardColumn[]) => {
    const previous = board?.columns ?? [];
    setBoard((current) => (current ? { ...current, columns } : current));
    try {
      await apiJson<{ ok: true }>(`/api/projects/${projectId}/cards/reorder`, {
        method: "PUT",
        body: JSON.stringify({
          columns: columns.map((column) => ({
            id: column.id,
            cardIds: column.cards.map((card) => card.id),
          })),
        }),
      });
    } catch (cause) {
      setBoard((current) => (current ? { ...current, columns: previous } : current));
      throw cause;
    }
  };

  return {
    board,
    loading,
    error,
    load,
    createCard,
    updateCard,
    deleteCard,
    createColumn,
    renameColumn,
    deleteColumn,
    addMember,
    removeMember,
    reorderCards,
  };
};
